import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import type { AxiosError } from "axios";
import axios from "axios";
import { AccordionCardWithEditFeatures } from "../../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import {
  TertiaryButtonMediumV2,
  DeleteButton,
  ButtonWithConfirmDialog,
  TertiaryButtonLargeV2,
  PrimaryButtonLargeV2,
  IconButtonMedium,
} from "../../../../../components/Buttons/Buttons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { Table } from "../../../../../components/Table/Table";
import { TablePlaceholder, useStoreState } from "../../../../../util/util";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { PlusIcon, EditIcon } from "../../../../../components/Icons/Icons";
import type { OptionType, UUID } from "../../../../../types/types";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { Controller, useForm } from "react-hook-form";
import { Description, SectionTitle, SettingRow } from "./TemplateSettings";
import { AttributeTemplateSchema } from "../../../../../types/types.PIM";
import { AttributeType } from "../../../../SharedPages/ProductDetailPage/Activity/types";
import { useAuthContext } from "../../../../../components/Auth";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import ReactTooltip from "react-tooltip";

export const ButtonContainer = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  border-radius: 25px;
  border: ${({ theme }) => theme.resolve("border_md")} solid transparent;
  align-items: center;
  justify-content: center;
  padding: 3px;
  path{
    fill: #fff;
    stroke: ${({ theme }) => theme.primaryTextColor};
    }
  &:active,
  &:focus,
  &:hover {
    background: ${({ theme }) => theme.primaryButtonHover};
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.primaryButtonBorder};
    path {
      stroke: ${({ theme }) => theme.primaryTextColor};
      fill: #fff;
    }
  }
  }
`;

const AddButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
  svg {
    width: 16px !important;
    height: 16px !important;
  }
`;
const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;
interface PreviewAttribute {
  id: string;
  name: string;
  display_name: string;
  input_type: AttributeType;
}

interface CompanyHierarchySettingsData {
  enabled: boolean;
  parent_preview_attributes: PreviewAttribute[];
  subsidiary_preview_attributes: PreviewAttribute[];
}

const MAX_PREVIEW_ATTRIBUTES = 5;

const ToggleDescription = styled.p`
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  margin: 0 0 ${({ theme }) => theme.resolve("spacing_lg")};
`;

const PreviewAttributesSection = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_2xl")};
  h3 {
    font-size: ${({ theme }) => theme.fontSizes.regular};
  }
`;

const AttributeTypeLabel = styled.span`
  text-transform: capitalize;
`;

type AddAttributeFormValues = {
  attribute_name: string;
  attribute_type: OptionType<AttributeType>;
};
interface CompanyHierarchySettingsProps {
  templateID: UUID;
  handleSettingChange: (
    field: keyof AttributeTemplateSchema,
    value: boolean
  ) => Promise<void>;
  defaultChecked: boolean;
  templateAttributes: {
    value: string;
    label: string;
    display_name: string;
    input_type: string;
    id: string;
  }[];
}
// ─── Sub-component: Preview Attributes Table ──────────────────────────────────

const PreviewAttributesTable = ({
  title,
  description,
  attributes,
  onAdd,
  onEdit,
  onDelete,
  maxReached,
}: {
  title: string;
  description: string;
  attributes: PreviewAttribute[];
  onAdd: () => void;
  onEdit: (attr: PreviewAttribute) => void;
  onDelete: (id: string) => void;
  maxReached: boolean;
}) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthContext();
  const columns = useMemo(
    () => [
      {
        header: t("Attribute Name"),
        accessorKey: "name",
        id: "name",
        cell: ({ row }: { row: { original: PreviewAttribute } }) => (
          <span>{row.original.display_name}</span>
        ),
      },
      {
        header: t("Attribute Type"),
        accessorKey: "input_type",
        id: "input_type",
        cell: ({ row }: { row: { original: PreviewAttribute } }) => (
          <AttributeTypeLabel>
            {row.original.input_type.replace("_", " ")}
          </AttributeTypeLabel>
        ),
      },
      {
        header: "",
        accessorKey: " ",
        id: "actions",
        cell: ({ row }: { row: { original: PreviewAttribute } }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <ButtonWithConfirmDialog
              useV2Buttons={true}
              Button={DeleteButton}
              testid={`delete-attr-${row.original.id}`}
              handleConfirm={() => onDelete(row.original.id)}
              confirmMessage={t(
                "Are you sure you want to remove this preview attribute?"
              )}
              disabled={
                row.original.name === "Company Name" ||
                !hasPermission("modify_templates")
              }
            />
            <IconButtonMedium
              onClick={() => onEdit(row.original)}
              style={{ border: "none", background: "transparent" }}
              disabled={
                row.original.name === "Company Name" ||
                !hasPermission("modify_templates")
              }
            >
              <ButtonContainer>
                <EditIcon height={16} width={16} />
              </ButtonContainer>
            </IconButtonMedium>
          </div>
        ),
      },
    ],
    [t, onDelete, onEdit, hasPermission]
  );

  return (
    <PreviewAttributesSection>
      <AccordionCardWithEditFeatures title={title} expanded={true}>
        <ToggleDescription>{description}</ToggleDescription>
        <Table
          columns={columns}
          data={attributes}
          isLoading={false}
          error={undefined}
          Placeholder={
            <TablePlaceholder
              message={t("No preview attributes configured.")}
            />
          }
        />
        <AddButtonWrapper>
          <TertiaryButtonMediumV2
            onClick={onAdd}
            disabled={maxReached || !hasPermission("modify_templates")}
            datatip={
              maxReached
                ? t("You have reached the maximum number of attributes")
                : !hasPermission("modify_templates")
                ? t("You do not have permission to add attributes")
                : undefined
            }
            datafor="modify-templates-tooltip"
          >
            <PlusIcon />
            {t("Add")}
          </TertiaryButtonMediumV2>
          {(maxReached || !hasPermission("modify_templates")) && (
            <ReactTooltip id="modify-templates-tooltip" />
          )}
        </AddButtonWrapper>
      </AccordionCardWithEditFeatures>
    </PreviewAttributesSection>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const CompanyHierarchySettings = ({
  templateID,
  handleSettingChange,
  defaultChecked,
  templateAttributes,
}: CompanyHierarchySettingsProps) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const [editingAttr, setEditingAttr] = useState<PreviewAttribute | null>(null);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const baseUrl = `/v2/tenants/${tenant_id}/mdm/templates/${templateID}/settings/company-hierarchy`;
  const settingsEndpoint = `${baseUrl}/preview-attributes`;
  const { data: settingsData, mutate } =
    useSWR<CompanyHierarchySettingsData>(settingsEndpoint);

  const parentAttrs = settingsData?.parent_preview_attributes ?? [];
  const subsidiaryAttrs = settingsData?.subsidiary_preview_attributes ?? [];
  const [showAddSlideout, setShowAddSlideout] =
    useState<"parent" | "subsidiary" | null>(null);
  const { handleSubmit, errors, reset, formState, control } =
    useForm<AddAttributeFormValues>();

  const handleDeleteAttr = useCallback(
    async (id: string) => {
      try {
        await axios.delete(`${baseUrl}/preview-attributes/${id}`);
        notifySuccess(t("Attribute removed successfully"));
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(errorMessage ?? t("Failed to remove attribute"), { error });
      } finally {
        mutate();
      }
    },
    [baseUrl, mutate, notifySuccess, notifyError, t]
  );

  const handleAddAttrSubmit = async (values: any) => {
    const selected = values.attribute;

    if (!selected) return;

    const scope = showAddSlideout === "parent" ? "parent" : "subsidiary";
    const currentList = scope === "parent" ? parentAttrs : subsidiaryAttrs;

    if (!editingAttr && currentList.length >= MAX_PREVIEW_ATTRIBUTES) return;

    const exists = currentList.some((a) => a.name === selected.label);

    if (exists) {
      notifyError(t("Attribute already added"));
      return;
    }

    try {
      if (editingAttr) {
        await axios.patch(`${baseUrl}/preview-attributes/${editingAttr.id}`, {
          scope,
          attribute_id: selected.id,
        });
      } else {
        await axios.post(`${baseUrl}/preview-attributes`, {
          scope,
          attribute_id: selected.id,
        });
      }
      notifySuccess(t("Attribute saved successfully"));
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(errorMessage ?? t("Failed to save attribute"), { error });
    } finally {
      mutate();
    }
    reset();
    setEditingAttr(null);
    setShowAddSlideout(null);
  };

  return (
    <>
      <SectionTitle>{t("Company Hierarchy")}</SectionTitle>
      <Description>
        {t(
          "Enable/Disable the company hierarchy configuration for this template"
        )}
      </Description>

      <SettingRow>
        <ToggleSwitchV2
          label={t("Company Hierarchy")}
          name="enable_company_hierarchy"
          checked={defaultChecked}
          onChange={(e) =>
            handleSettingChange("enable_company_hierarchy", e.target.checked)
          }
        />
      </SettingRow>
      {defaultChecked && (
        <>
          <PreviewAttributesTable
            title={t("Configure Parent Company Preview Attributes")}
            description={t(
              "Configure which attributes to display when previewing the Parent Company"
            )}
            attributes={parentAttrs}
            onAdd={() => setShowAddSlideout("parent")}
            onEdit={(attr) => {
              setEditingAttr(attr);
              setShowAddSlideout("parent");
            }}
            onDelete={handleDeleteAttr}
            maxReached={parentAttrs.length >= MAX_PREVIEW_ATTRIBUTES}
          />
          <PreviewAttributesTable
            title={t("Configure Subsidiary Company Preview Attributes")}
            description={t(
              "Configure which attributes to display when previewing Subsidiary Companies"
            )}
            attributes={subsidiaryAttrs}
            onAdd={() => setShowAddSlideout("subsidiary")}
            onEdit={(attr) => {
              setEditingAttr(attr);
              setShowAddSlideout("subsidiary");
            }}
            onDelete={handleDeleteAttr}
            maxReached={subsidiaryAttrs.length >= MAX_PREVIEW_ATTRIBUTES}
          />
        </>
      )}
      <SlideOut
        title={
          showAddSlideout === "parent"
            ? t("Add Parent Preview Attribute")
            : t("Add Subsidiary Preview Attribute")
        }
        show={showAddSlideout !== null}
        closeFlyout={() => {
          reset();
          setEditingAttr(null);
          setShowAddSlideout(null);
        }}
      >
        <form onSubmit={handleSubmit(handleAddAttrSubmit)}>
          <Controller
            as={SelectBoxV2}
            control={control}
            name="attribute"
            placeholder={t("Select Attribute")}
            options={templateAttributes}
            errors={errors}
            defaultValue={
              editingAttr
                ? { label: editingAttr.name, value: editingAttr.id }
                : null
            }
            formState={formState}
          />
          <ActionButtonsWrapper>
            <TertiaryButtonLargeV2
              type="button"
              fullWidth
              onClick={() => {
                reset();
                setEditingAttr(null);
                setShowAddSlideout(null);
              }}
            >
              {t("Cancel")}
            </TertiaryButtonLargeV2>
            <PrimaryButtonLargeV2 fullWidth type="submit">
              {t("Add")}
            </PrimaryButtonLargeV2>
          </ActionButtonsWrapper>
        </form>
      </SlideOut>
    </>
  );
};
